import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  FormControlLabel,
  Grid2 as Grid,
  MenuItem,
  Radio,
  TextField,
} from "@mui/material";
import ValidationError from "@components/ValidationError/ValidationError";
import { UseFormReturn } from "react-hook-form";
import { Account, AccountType, InstitutionType } from "@models/account";
import ReactHookFormSelect from "@components/ReactHookFormSelect/ReactHookFormSelect";
import { Country } from "@models/country";
import ReactHookFormRadioButtonGroup from "@components/ReactHookFormRadioButtonGroup/ReactHookFormRadioButtonGroup";
import styled from "@emotion/styled";
import { Language } from "@models/language";
import CustomLabelInput from "@components/CustomLabelInput";
import { User } from "@models/user";
import ReactHookFormSwitch from '@components/ReactHookFormSwitch';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RadioButtonLabel = styled.div`
  text-align: left;
`;

const RadioButtonLabelSecondaryText = styled.div`
  color: #757575;
`;

const AccountForm: FC<{ form: UseFormReturn<Partial<Account & User>> }> = ({
  form,
}) => {
  const { t } = useTranslation();

  const countries = Object.entries(Country).sort((c1, c2) =>
    t(c1[0], { ns: "country" }).localeCompare(t(c2[0], { ns: "country" })),
  );

  const languages = Object.entries(Language).sort((l1, l2) =>
    t(l1[0], { ns: "language" })?.localeCompare(t(l2[0], { ns: "language" })),
  );

  const {
    control,
    register,
    formState: { errors, disabled },
    watch,
  } = form;

  const accountType = watch("type");

  return (
    <Container>
      <Grid container spacing={2}>
        <Grid size={6}>
          <CustomLabelInput
            label={t("account.firstNameInput.label")}
            id="firstName"
            required={true}
          >
            <TextField
              {...register("firstName", { required: true })}
              id="firstName"
              error={!!errors?.firstName}
              type="text"
              size="small"
              variant="outlined"
              fullWidth
              placeholder={t("account.firstNameInput.placeholder")}
              disabled={disabled}
            />
          </CustomLabelInput>
        </Grid>

        <Grid size={6}>
          <CustomLabelInput
            label={t("account.lastNameInput.label")}
            id="lastName"
            required={true}
          >
            <TextField
              {...register("lastName", { required: true })}
              id="lastName"
              error={!!errors?.lastName}
              type="text"
              size="small"
              variant="outlined"
              fullWidth
              placeholder={t("account.lastNameInput.placeholder")}
              disabled={disabled}
            />
          </CustomLabelInput>
        </Grid>
      </Grid>

      <ReactHookFormSelect
        size="small"
        fullWidth
        label={t("account.language.label")}
        placeholder={t("account.language.placeholder")}
        displayValueFunction={(v: string) => t(v, { ns: "language" })}
        controller={{
          control,
          rules: { required: true },
          name: "language",
          defaultValue: "",
        }}
        error={!!errors?.language}
        required={true}
      >
        {languages.map(([key, value]) => (
          <MenuItem key={key} value={value}>
            {t(key, { ns: "language" })}
          </MenuItem>
        ))}
      </ReactHookFormSelect>

      <ReactHookFormSelect
        size="small"
        fullWidth
        label={t("account.countrySelect.label")}
        placeholder={t("account.countrySelect.placeholder")}
        displayValueFunction={(v: string) => t(v, { ns: "country" })}
        controller={{
          control,
          rules: { required: true },
          name: "country",
          defaultValue: "",
        }}
        required={true}
      >
        {countries.map(([key, value]) => (
          <MenuItem key={value} value={value}>
            {t(key, { ns: "country" })}
          </MenuItem>
        ))}
      </ReactHookFormSelect>

      <CustomLabelInput label={t("account.cityInput.label")} id="accountCity">
        <TextField
          {...register("city")}
          id="accountCity"
          error={!!errors?.city}
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          placeholder={t("account.cityInput.placeholder")}
          disabled={disabled}
        />
      </CustomLabelInput>

      <ReactHookFormSwitch
        id="weeklyReportsEnabled"
        controller={{
          name: "weeklyReportsEnabled",
          control,
          defaultValue: true,
        }}
        label={t("account.weeklyReports.label")}
        subtitle={t("account.weeklyReports.subTitle")}
      />

      <CustomLabelInput label={t("account.accountTypeLabel")}>
        <ReactHookFormRadioButtonGroup
          label=""
          controller={{
            control,
            rules: { required: true },
            name: "type",
            defaultValue: AccountType.Individual,
          }}
        >
          <Grid container spacing={2}>
            {Object.entries(AccountType).map(([key, value]) => (
              <Grid key={key} size={12}>
                <FormControlLabel
                  value={value}
                  control={<Radio disabled={disabled} />}
                  label={
                    <RadioButtonLabel>
                      <div>{t(`accountType.${key}`)}</div>
                      <RadioButtonLabelSecondaryText>
                        {t(`account.accountType.${key}.description`)}
                      </RadioButtonLabelSecondaryText>
                    </RadioButtonLabel>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </ReactHookFormRadioButtonGroup>

        {accountType === AccountType.Organization && (
          <>
            <CustomLabelInput
              label={t("account.institutionNameInput.placeholder")}
              id="accountInstitutionName"
              required={true}
            >
              <TextField
                {...register("institutionName", { required: true })}
                id="accountInstitutionName"
                error={!!errors?.institutionName}
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("account.institutionNameInput.placeholder")}
                disabled={disabled}
              />
            </CustomLabelInput>

            <ReactHookFormSelect
              fullWidth
              size="small"
              label={t("account.institutionTypeSelect.label")}
              placeholder={t("account.institutionTypeSelect.placeholder")}
              controller={{
                control,
                rules: { required: true },
                name: "institutionType",
                defaultValue: "",
              }}
              error={!!errors?.institutionType}
              required={true}
            >
              {Object.entries(InstitutionType).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {t(`institutionType.${key}`)}
                </MenuItem>
              ))}
            </ReactHookFormSelect>

            <CustomLabelInput
              label={t("account.numberPatientsInput.label")}
              id="accountNumberPatients"
              required={true}
            >
              <TextField
                {...register("numberPatients", {
                  required: true,
                  valueAsNumber: true,
                })}
                id="accountNumberPatients"
                error={!!errors?.numberPatients}
                type="number"
                size="small"
                variant="outlined"
                fullWidth
                placeholder={t("account.numberPatientsInput.placeholder")}
                disabled={disabled}
              />
            </CustomLabelInput>
          </>
        )}
      </CustomLabelInput>

      {errors.root && (
        <ValidationError>{t(`errors.${errors.root.type}`)}</ValidationError>
      )}
    </Container>
  );
};

export default AccountForm;
